import styled from '../../../~reusables/contexts/ThemeContext';
import { TransitionLink } from '../../atoms/TransitionLink';

// Keep styling-only $props off the DOM when wrapping components.
export const isNotTransientProp = (prop: string) => !prop.startsWith('$');

export const MOBILE_BREAKPOINT = 768;
export const mobileMediaQuery = `@media (max-width: ${MOBILE_BREAKPOINT}px)`;

export const PageWrapper = styled.div`
  min-height: 100vh;
  background: var(--layout-page-bg);
  display: flex;
  flex-direction: column;
`;

export const NavHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--nav-bg);
  border-bottom: 1px solid var(--nav-border);
`;

export const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 56px;
  padding: 0 24px;
  min-width: 0;

  ${mobileMediaQuery} {
    padding: 0 12px;
  }
`;

export const NavSpacer = styled.div`
  flex: 1 1 8px;
  min-width: 8px;
`;

export const NavCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const LogoLink = styled(TransitionLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;

  &:hover {
    background: var(--nav-hover-bg);
  }
`;

export const CrumbSeparator = styled.span`
  color: var(--nav-separator);
  font-size: 22px;
  font-weight: 300;
  line-height: 1;
  user-select: none;
  flex-shrink: 0;
`;

export const CrumbButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 320px;
  height: 36px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--nav-text);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover,
  &[aria-expanded='true'] {
    background: var(--nav-hover-bg);
  }

  &:focus-visible {
    outline: 2px solid var(--nav-accent);
    outline-offset: 1px;
  }

  ${mobileMediaQuery} {
    max-width: 180px;
    padding: 0 6px;
  }
`;

export const CrumbLink = styled(TransitionLink)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  color: var(--nav-text);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: var(--nav-hover-bg);
    color: var(--nav-text);
  }
`;

export const StaticCrumb = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  color: var(--nav-text);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CrumbText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CrumbMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: var(--nav-text-subtle);

  ${mobileMediaQuery} {
    display: none;
  }
`;

export const AppIconImage = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const AppIconFallback = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  flex-shrink: 0;
  background: var(--nav-hover-bg);
  color: var(--nav-text-muted);
  font-size: 10px;
  font-weight: 700;
`;

export const IconButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--nav-text-muted);
  cursor: pointer;
  flex-shrink: 0;

  &:hover,
  &[aria-expanded='true'] {
    background: var(--nav-hover-bg);
    color: var(--nav-text);
  }

  &:focus-visible {
    outline: 2px solid var(--nav-accent);
    outline-offset: 1px;
  }
`;

export const StatusDot = styled.span<{ $color: string }>`
  position: absolute;
  top: 7px;
  right: 7px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  box-shadow: 0 0 0 2px var(--nav-bg);
`;

export const AvatarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: 4px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  overflow: hidden;
  background: var(--nav-hover-bg);
  color: var(--nav-text-muted);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover,
  &[aria-expanded='true'] {
    box-shadow: 0 0 0 3px var(--nav-selected-bg);
  }

  &:focus-visible {
    outline: 2px solid var(--nav-accent);
    outline-offset: 2px;
  }
`;

export const SearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 220px;
  height: 34px;
  padding: 0 10px;
  margin-right: 4px;
  border: 1px solid var(--nav-input-border);
  border-radius: 8px;
  background: var(--nav-input-bg);
  color: var(--nav-text-subtle);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    border-color: var(--nav-separator);
    color: var(--nav-text-muted);
  }

  &:focus-visible {
    outline: 2px solid var(--nav-accent);
    outline-offset: 1px;
  }

  @media (max-width: 1100px) {
    width: 36px;
    padding: 0;
    justify-content: center;
    border-color: transparent;
    background: transparent;

    .search-label {
      display: none;
    }
  }
`;

export const Kbd = styled.kbd`
  margin-left: auto;
  padding: 0 5px;
  border: 1px solid var(--nav-input-border);
  border-radius: 4px;
  background: var(--nav-bg);
  color: var(--nav-text-muted);
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 16px;
`;

export const TabsRow = styled.nav`
  display: flex;
  gap: 24px;
  padding: 0 24px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mobileMediaQuery} {
    gap: 20px;
    padding: 0 16px;
  }
`;

export const TabLink = styled(TransitionLink, {
  shouldForwardProp: isNotTransientProp,
})<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 2px;
  margin-bottom: -1px;
  border-bottom: 2px solid
    ${(p) => (p.$active ? 'var(--nav-text)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--nav-text)' : 'var(--nav-text-muted)')};
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 500 : 400)};
  white-space: nowrap;

  &:hover {
    color: var(--nav-text);
  }

  &:focus-visible {
    outline: 2px solid var(--nav-accent);
    outline-offset: -2px;
    border-radius: 4px;
  }
`;

export const CountBadge = styled.span`
  min-width: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--nav-running-bg);
  color: var(--nav-running-text);
  font-size: 11px;
  font-weight: 500;
  line-height: 18px;
  text-align: center;
`;

export const ContentContainer = styled.main`
  flex: 1;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px;
  box-sizing: border-box;

  ${mobileMediaQuery} {
    padding: 16px;
  }
`;

// Page header used for sections with their own sub-navigation (settings) and
// for the build picker on build pages.
export const PageHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: var(--layout-card-title);
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.25;
`;

export const SubTabsSelect = styled.div`
  display: none;

  ${mobileMediaQuery} {
    display: block;

    .ant-select {
      width: 100%;
      height: 40px;
    }
  }
`;

export const SubTabsNav = styled.nav`
  display: inline-flex;
  align-self: flex-start;
  max-width: 100%;
  gap: 4px;
  padding: 4px;
  border-radius: 10px;
  background: var(--nav-subtabs-bg);
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mobileMediaQuery} {
    display: none;
  }
`;

export const SubTabLink = styled(TransitionLink, {
  shouldForwardProp: isNotTransientProp,
})<{ $active?: boolean }>`
  padding: 6px 12px;
  border-radius: 7px;
  background: ${(p) =>
    p.$active ? 'var(--nav-subtab-active-bg)' : 'transparent'};
  box-shadow: ${(p) => (p.$active ? '0 1px 2px rgba(0, 0, 0, 0.08)' : 'none')};
  color: ${(p) => (p.$active ? 'var(--nav-text)' : 'var(--nav-text-muted)')};
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 500 : 400)};
  white-space: nowrap;

  &:hover {
    color: var(--nav-text);
  }

  &:focus-visible {
    outline: 2px solid var(--nav-accent);
    outline-offset: 1px;
  }
`;

export const MenuHeader = styled.div`
  padding: 10px 12px 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const MenuRow = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const MenuRowText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MenuRowMeta = styled.span`
  margin-left: auto;
  color: var(--nav-text-subtle);
  font-size: 12px;
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const HideOnMobile = styled.span`
  display: contents;

  ${mobileMediaQuery} {
    display: none;
  }
`;

export const ExtrasSlot = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
`;
